import { action, makeAutoObservable, reaction } from "mobx";

import { ValidationResult, ValidationSchema, BlockPayload } from "./types";
import { getValueByPath } from "./utils";

export class ValidationStore<T> {
  result: ValidationResult = {
    isValid: true,
    errors: {},
    warnings: {},
  };

  // Track all validation errors regardless of display state
  private allErrors: Record<string, string[]> = {};
  private allWarnings: Record<string, string[]> = {};

  hasValidated = false;
  touchedFields = new Set<string>();
  hasTriggeredSave = false;

  private timeoutId: NodeJS.Timeout | null = null;
  private disposer: (() => void) | null = null;

  constructor(
    private schema: ValidationSchema<BlockPayload<T>>,
    private getPayload: () => BlockPayload<T> | undefined,
    private debounceMs = 300,
  ) {
    makeAutoObservable(this, {
      validate: action,
      triggerSaveValidation: action,
      markFieldAsTouched: action,
      reset: action,
      dispose: false,
    });

    // Set up reaction to validate on payload changes
    this.setupPayloadReaction();
  }

  private setupPayloadReaction(): void {
    this.disposer = reaction(
      () => {
        const payload = this.getPayload();
        if (!payload) {
          return null;
        }

        // Return a serialized version to ensure deep comparison
        return JSON.stringify(payload);
      },
      () => {
        // Only validate if user has interacted with fields or save was triggered
        if (this.touchedFields.size > 0 || this.hasTriggeredSave) {
          this.debouncedValidate();
        }
      },
      {
        // Don't fire on initial run to avoid premature validation
        fireImmediately: false,
      },
    );
  }

  private debouncedValidate = (): void => {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      this.validate();
    }, this.debounceMs);
  };

  validate = (forceValidate = false): ValidationResult => {
    const payload = this.getPayload();

    if (!payload) {
      const emptyResult = { isValid: true, errors: {}, warnings: {} };
      this.setResult(emptyResult);
      return emptyResult;
    }

    const errors: Record<string, string[]> = {};
    const warnings: Record<string, string[]> = {};

    // Reset internal error/warning tracking
    this.allErrors = {};
    this.allWarnings = {};

    // Sort rules by dependencies to ensure proper evaluation order
    const sortedRules = this.sortRulesByDependencies();

    for (const rule of sortedRules) {
      const value = getValueByPath(payload, rule.path);
      const isValid = rule.validate(value, payload, payload.allSources);

      if (!isValid) {
        const message = this.getValidationMessage(rule, value, payload);
        const severity = rule.severity || "error";

        // Always track all errors/warnings for internal validation state
        const allTarget = severity === "error" ? this.allErrors : this.allWarnings;
        if (!allTarget[rule.path]) {
          allTarget[rule.path] = [];
        }
        allTarget[rule.path].push(message);

        // Only add to display errors/warnings if should show
        const shouldShowError = this.shouldShowValidationMessage(rule.path, forceValidate);

        if (shouldShowError) {
          const target = severity === "error" ? errors : warnings;

          if (!target[rule.path]) {
            target[rule.path] = [];
          }
          target[rule.path].push(message);
        }
      }
    }

    const validationResult: ValidationResult = {
      // Use all errors for actual validation state, not just displayed errors
      isValid: Object.keys(this.allErrors).length === 0,
      errors, // Only displayed errors
      warnings, // Only displayed warnings
    };

    this.setResult(validationResult);
    this.setHasValidated(true);

    return validationResult;
  };

  private sortRulesByDependencies() {
    return [...this.schema.rules].sort((a, b) => {
      if (a.dependencies?.includes(b.path)) {
        return 1;
      }
      if (b.dependencies?.includes(a.path)) {
        return -1;
      }
      return 0;
    });
  }

  private shouldShowValidationMessage(path: string, forceValidate: boolean): boolean {
    const shouldShow = forceValidate || this.hasTriggeredSave || this.touchedFields.has(path);

    return shouldShow;
  }

  private getValidationMessage(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rule: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    payload: BlockPayload<T>,
  ): string {
    return typeof rule.message === "function" ? rule.message(value, payload) : rule.message;
  }

  triggerSaveValidation = (): ValidationResult => {
    this.setHasTriggeredSave(true);
    return this.validate(true);
  };

  markFieldAsTouched = (path: string): void => {
    this.touchedFields.add(path);
  };

  reset = (): void => {
    this.result = {
      isValid: true,
      errors: {},
      warnings: {},
    };
    this.allErrors = {};
    this.allWarnings = {};
    this.hasValidated = false;
    this.touchedFields.clear();
    this.hasTriggeredSave = false;
  };

  // Computed-like getters for field-specific validation state
  getFieldError = (path: string): string | undefined =>
    // Use display errors for UI messages (respects touched field logic)
    this.result.errors[path]?.[0];

  getFieldWarning = (path: string): string | undefined =>
    // Use display warnings for UI messages (respects touched field logic)
    this.result.warnings[path]?.[0];

  isFieldValid = (path: string): boolean =>
    // Use internal error tracking for actual validation state
    !this.allErrors[path];

  hasFieldWarning = (path: string): boolean =>
    // Use internal warning tracking for actual validation state
    !!this.allWarnings[path];

  // Private action methods
  private setResult = (result: ValidationResult): void => {
    this.result = result;
  };

  private setHasValidated = (value: boolean): void => {
    this.hasValidated = value;
  };

  private setHasTriggeredSave = (value: boolean): void => {
    this.hasTriggeredSave = value;
  };

  dispose = (): void => {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.disposer) {
      this.disposer();
      this.disposer = null;
    }
  };
}
